/* eslint-disable complexity */
/* eslint-disable no-console */
import { Vector3 } from 'three';
import { IBuildingProviderContext } from '../../@types/ContextProviderRelated';
import { EWall } from '../../@types/Enums';
import { BOUNDARY_TYPE } from '../../models/abstract/Boundary';
import BoundaryEdge from '../../models/abstract/BoundaryEdge';
import Wall from '../../models/abstract/Wall';
import { IRelation } from '../../models/Relation';
import BuildingLevelModel from '../../models/standard/BuildingLevelModel';
import BuildingModel, { IMultiRoomBuilding } from '../../models/standard/BuildingModel';
import CameraPositionSpriteModel from '../../models/standard/CameraPositionSpriteModel';
import CompassModel from '../../models/standard/CompassModel';
import DoorModel from '../../models/standard/DoorModel';
import ElementModel from '../../models/standard/ElementModel';
import FloorModel from '../../models/standard/FloorModel';
import HotspotModel from '../../models/standard/HotspotModel';
import Model2DModel from '../../models/standard/Model2DModel';
import PassageModel from '../../models/standard/PassageModel';
import PartitionWallModel from '../../models/standard/PartitionWallModel';
import PhysicalBoundaryModel from '../../models/standard/PhysicalBoundary';
import PlanModel from '../../models/standard/PlanModel';
import ProductModel from '../../models/standard/ProductModel';
import RadiatorModel from '../../models/standard/RadiatorModel';
import RoomModel from '../../models/standard/RoomModel';
import SimpleMeasurementModel from '../../models/standard/SimpleMeasurementModel';
import SpaceModel from '../../models/standard/SpaceModel';
import StandardWallModel from '../../models/standard/StandardWallModel';
import TilesModel from '../../models/standard/TileModel';
import VirtualBoundaryModel from '../../models/standard/VirtualBoundaryModel';
import WindowModel from '../../models/standard/WindowModel';
import { APP_VERSION, JSON_VERSION } from '../constants';
import { normalizeBuildingTopology } from '../normalizeBuildingTopology';
import { NonFetchableEntity } from '../planJsonExportImportUtils';
import { createAttributes, createObject, createObjects } from '../transformation/tranformToMultiRoomBuilding';
import { transformMultiRoomStructure } from '../transformation/transformMultiRoomStructure';
import deserializeHotspot from './deserializeHotspot';
import deserializeMeasurement from './deserializeMeasurement';
import { fetchProductById } from './fetchProductById';
import { instantiateElementAsync } from './instantiateElementAsync';
import { instantiateModel2DAsync } from './instantiateModel2DAsync';

const EPS = 1e-6;
const CHAIN_EPS = 0.05;
const INSANE_COORD_LIMIT = 10000;

type Side = 'A' | 'B';

type RawWallGeom = {
  rawUuid: string;
  depth: number;
  outward: Vector3;
  innerStart: Vector3;
  innerEnd: Vector3;
  outerStart: Vector3;
  outerEnd: Vector3;
  centerStartBase: Vector3;
  centerEndBase: Vector3;
};

type WallUsageRecord = {
  boundaryUuid: string;
  edgeUuid?: string;

  /**
   * Legacy source wall uuid from the V2 JSON.
   */
  wallUuid: string;

  /**
   * New wall uuid after V2 migration.
   *
   * Usually this is the same as wallUuid, but if the same legacy wall is used by
   * different spaces with incompatible centerline segments, we split it into
   * per-boundary/per-edge wall instances to keep every room dimension valid.
   */
  resolvedWallUuid?: string;

  side: Side;
  reversed: boolean;
  faceCornerStart: Vector3;
  faceCornerEnd: Vector3;
  centerCornerStart: Vector3;
  centerCornerEnd: Vector3;
};

type BoundaryUsageBucket = {
  key: string;
  roomUuid: string;
  spaceUuid: string;
  boundaryUuid: string;
  rawEdgeCount: number;
  records: WallUsageRecord[];
};

type RawBoundaryEntry = {
  edgeUuid?: string;
  wallUuid: string;
  side: Side;
  reversed: boolean;
  explicitStart: Vector3 | null;
  explicitEnd: Vector3 | null;
  faceTraversalStart: Vector3;
  faceTraversalEnd: Vector3;
  centerTraversalStart: Vector3;
  centerTraversalEnd: Vector3;
};

function signedArea2D(points: Vector3[]): number {
  if (points.length < 3) return 0;

  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    area += a.x * b.y - b.x * a.y;
  }

  return area / 2;
}

function reverseRecordTraversal(rec: WallUsageRecord): WallUsageRecord {
  return {
    ...rec,
    reversed: !rec.reversed,
    faceCornerStart: rec.faceCornerEnd.clone(),
    faceCornerEnd: rec.faceCornerStart.clone(),
    centerCornerStart: rec.centerCornerEnd.clone(),
    centerCornerEnd: rec.centerCornerStart.clone(),
  };
}

/**
 * Canonical import invariant:
 * every physical boundary is stored clockwise and every edge.startPoint/endPoint
 * follows exactly that clockwise traversal.
 *
 * IFC is fine with the face polygon, but our UI angle indicators assume a stable
 * ordered loop. If imported V2 boundaries keep mixed CW/CCW or partially flipped
 * traversal, the floor can look correct while WallAngleIndicator receives
 * contradictory prev/next directions.
 */
function ensureClockwiseUsageRecords(records: WallUsageRecord[]): WallUsageRecord[] {
  if (records.length < 3) return records;

  const area = signedArea2D(records.map((rec) => rec.faceCornerStart));

  // Positive shoelace area = CCW in our XY plane. Store CW.
  if (area > 0) {
    return [...records].reverse().map(reverseRecordTraversal);
  }

  return records;
}

const vec = (p: any) => new Vector3(p?.x ?? 0, p?.y ?? 0, p?.z ?? 0);

function clamp01(t: number) {
  return Math.max(0, Math.min(1, t));
}

function samePt(a: Vector3, b: Vector3, eps = CHAIN_EPS) {
  return Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;
}

function isPhysicalBoundaryType(type: any) {
  return type === BOUNDARY_TYPE.PHYSICAL || type === 'PHYSICAL' || type === 0;
}

function isPartitionRaw(raw: any) {
  return raw?.type === EWall.PARTITION || raw?.type === 'partition';
}

function isStandardWallInstance(w: Wall | undefined | null): w is StandardWallModel {
  return (
    !!w &&
    !(w instanceof PartitionWallModel) &&
    (w instanceof StandardWallModel || w.type === EWall.STANDARD || w.type === undefined || w.type === null)
  );
}

function lineIntersection2D(a1: Vector3, a2: Vector3, b1: Vector3, b2: Vector3): Vector3 | null {
  const x1 = a1.x;
  const y1 = a1.y;
  const x2 = a2.x;
  const y2 = a2.y;
  const x3 = b1.x;
  const y3 = b1.y;
  const x4 = b2.x;
  const y4 = b2.y;

  const den = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(den) < EPS) return null;

  const px = ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / den;
  const py = ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / den;

  return new Vector3(px, py, 0);
}

/**
 * Minimal safety guard.
 *
 * This is the important fix for the 51038.x coordinate bug:
 * we still prefer the proper V2 wall-face intersection, but we never accept an
 * intersection that is absurdly far away because two almost-parallel lines were intersected.
 *
 * Important: this does NOT make cached edge.startPoint/endPoint the primary geometry.
 * They are only allowed as fallback points.
 */
function safeLineIntersection2D(
  a1: Vector3,
  a2: Vector3,
  b1: Vector3,
  b2: Vector3,
  fallback: Vector3,
): Vector3 {
  const intersection = lineIntersection2D(a1, a2, b1, b2);
  if (!intersection) return fallback.clone();

  if (
    !Number.isFinite(intersection.x) ||
    !Number.isFinite(intersection.y) ||
    Math.abs(intersection.x) > INSANE_COORD_LIMIT ||
    Math.abs(intersection.y) > INSANE_COORD_LIMIT
  ) {
    return fallback.clone();
  }

  const lenA = a1.distanceTo(a2);
  const lenB = b1.distanceTo(b2);
  const maxReasonableDistance = Math.max(10, lenA, lenB) * 5;

  if (
    intersection.distanceTo(a2) > maxReasonableDistance ||
    intersection.distanceTo(b1) > maxReasonableDistance
  ) {
    return fallback.clone();
  }

  return intersection;
}

function pointOnSegment(a: Vector3, b: Vector3, t: number): Vector3 {
  return a.clone().lerp(b, clamp01(t));
}

function projectParamOnLine(start: Vector3, end: Vector3, p: Vector3): number {
  const ab = end.clone().sub(start);
  const len2 = ab.lengthSq();
  if (len2 < EPS) return 0;
  return clamp01(p.clone().sub(start).dot(ab) / len2);
}

function hasInsanePoint(p: Vector3) {
  return (
    !Number.isFinite(p.x) ||
    !Number.isFinite(p.y) ||
    Math.abs(p.x) > INSANE_COORD_LIMIT ||
    Math.abs(p.y) > INSANE_COORD_LIMIT
  );
}

function hasInsaneBoundaryPoint(edges: BoundaryEdge[]): boolean {
  return edges.some((edge) => hasInsanePoint(edge.startPoint) || hasInsanePoint(edge.endPoint));
}

function getRawWallDepth(raw: any): number {
  if (typeof raw?.depth === 'number' && Number.isFinite(raw.depth) && raw.depth > 0) {
    return raw.depth;
  }

  if (Array.isArray(raw?.attributes)) {
    const attr = raw.attributes.find((a: any) => a?.key === 'Wandstärke' || a?.id === 28);
    if (attr?.value !== null && attr?.value !== undefined) {
      const num = Number(attr.value);
      if (Number.isFinite(num) && num > 0) return num / 100;
    }
  }

  return 0.15;
}

function getLegacyOutwardNormal(raw: any, innerStart: Vector3, innerEnd: Vector3): Vector3 {
  const wallDir = innerEnd.clone().sub(innerStart);
  if (wallDir.lengthSq() < EPS) return new Vector3(0, 1, 0);
  wallDir.normalize();

  const candidate = new Vector3(-wallDir.y, wallDir.x, 0);

  const outerStartPoint = raw?.outerStartPoint ?? raw?._v1OuterStart ?? null;
  if (outerStartPoint) {
    const test = vec(outerStartPoint).sub(innerStart);
    if (test.lengthSq() > EPS && test.dot(candidate) < 0) candidate.negate();
  }

  return candidate.normalize();
}

function moveListByShift(list: any[], shift: Vector3) {
  if (!Array.isArray(list)) return;

  list.forEach((obj) => {
    if (obj?.position) {
      obj.position.x += shift.x;
      obj.position.y += shift.y;
      if (typeof obj.position.z !== 'number') obj.position.z = 0;
    }
  });
}

function collectAllRawWalls(buildingData: IMultiRoomBuilding, buildingAny: any): Map<string, any> {
  const rawWallMap = new Map<string, any>();

  buildingData.levels.forEach((level) => {
    level.rooms.forEach((room) => {
      room.spaces.forEach((space) => {
        (space.walls ?? []).forEach((w: any) => {
          if (w?.uuid && !rawWallMap.has(w.uuid)) rawWallMap.set(w.uuid, w);
        });

        (space.boundaries ?? []).forEach((b: any) => {
          (b.walls ?? []).forEach((w: any) => {
            if (w?.uuid && !rawWallMap.has(w.uuid) && w.startPoint && w.endPoint) rawWallMap.set(w.uuid, w);
          });

          (b.edges ?? []).forEach((e: any) => {
            const ew = e?.wall;
            if (ew?.uuid && !rawWallMap.has(ew.uuid) && ew.startPoint && ew.endPoint) rawWallMap.set(ew.uuid, ew);
          });
        });
      });
    });

    (level as any).walls?.forEach((w: any) => {
      if (w?.uuid && !rawWallMap.has(w.uuid)) rawWallMap.set(w.uuid, w);
    });
  });

  (buildingAny.walls ?? []).forEach((w: any) => {
    if (w?.uuid && !rawWallMap.has(w.uuid)) rawWallMap.set(w.uuid, w);
  });

  return rawWallMap;
}

function computeRawWallGeom(raw: any): RawWallGeom {
  const depth = getRawWallDepth(raw);
  const innerStart = vec(raw.startPoint);
  const innerEnd = vec(raw.endPoint);
  const outward = getLegacyOutwardNormal(raw, innerStart, innerEnd);

  const outerStart = innerStart.clone().add(outward.clone().multiplyScalar(depth));
  const outerEnd = innerEnd.clone().add(outward.clone().multiplyScalar(depth));

  const centerStartBase = innerStart.clone().add(outward.clone().multiplyScalar(depth / 2));
  const centerEndBase = innerEnd.clone().add(outward.clone().multiplyScalar(depth / 2));

  return {
    rawUuid: raw.uuid,
    depth,
    outward,
    innerStart,
    innerEnd,
    outerStart,
    outerEnd,
    centerStartBase,
    centerEndBase,
  };
}

function createStandardWallWithResolvedCenterline(
  raw: any,
  geom: RawWallGeom,
  finalCenterStart: Vector3,
  finalCenterEnd: Vector3,
): StandardWallModel {
  const cloned = JSON.parse(JSON.stringify(raw));

  const assetShift = geom.outward.clone().multiplyScalar(geom.depth / 2);
  moveListByShift(cloned.windows, assetShift);
  moveListByShift(cloned.doors, assetShift);
  moveListByShift(cloned.passages, assetShift);
  moveListByShift(cloned.radiators, assetShift);
  if (cloned.sides?.A?.radiators) moveListByShift(cloned.sides.A.radiators, assetShift);
  if (cloned.sides?.B?.radiators) moveListByShift(cloned.sides.B.radiators, assetShift);

  const doors = (cloned.doors ?? []).map((door: any) => {
    const d = new DoorModel(
      new Vector3(door.position.x, door.position.y, door.position.z ?? 0),
      door.length,
      door.depth,
      door.angle,
      door.openingDirection,
      door.orientation,
      createAttributes(door.attributes),
    );
    d.setUUID(door.uuid);
    return d;
  });

  const windows = (cloned.windows ?? []).map((win: any) => {
    const w = new WindowModel(
      new Vector3(win.position.x, win.position.y, win.position.z ?? 0),
      win.length,
      win.depth,
      win.angle,
      createAttributes(win.attributes),
    );
    w.setUUID(win.uuid);
    return w;
  });

  const passages = (cloned.passages ?? []).map((passage: any) => {
    const p = new PassageModel(
      new Vector3(passage.position.x, passage.position.y, passage.position.z ?? 0),
      passage.length,
      passage.depth,
      passage.angle,
      createAttributes(passage.attributes),
    );
    p.setUUID(passage.uuid);
    return p;
  });

  const wall = new StandardWallModel(
    finalCenterStart.clone(),
    finalCenterEnd.clone(),
    geom.depth,
    windows,
    doors,
    [], 
    createAttributes(cloned.attributes),
    cloned.uuid,
    passages,
  );
  wall.setUUID(cloned.uuid);

  const innerStart = finalCenterStart.clone().add(geom.outward.clone().multiplyScalar(-geom.depth / 2));
  const innerEnd = finalCenterEnd.clone().add(geom.outward.clone().multiplyScalar(-geom.depth / 2));
  const outerStart = finalCenterStart.clone().add(geom.outward.clone().multiplyScalar(geom.depth / 2));
  const outerEnd = finalCenterEnd.clone().add(geom.outward.clone().multiplyScalar(geom.depth / 2));

  if ((wall as any).setOuterStartPoint) (wall as any).setOuterStartPoint(outerStart);
  if ((wall as any).setOuterEndPoint) (wall as any).setOuterEndPoint(outerEnd);
  if ((wall as any).setOuterStartPoint2) (wall as any).setOuterStartPoint2(innerStart);
  if ((wall as any).setOuterEndPoint2) (wall as any).setOuterEndPoint2(innerEnd);

  const radA: RadiatorModel[] = Array.isArray(cloned.sides?.A?.radiators)
    ? cloned.sides.A.radiators.map((rad: any) => {
        const r = new RadiatorModel(
          new Vector3(rad.position.x, rad.position.y, rad.position.z ?? 0),
          rad.length,
          rad.depth,
          rad.angle,
          createAttributes(rad.attributes),
          undefined,
          rad.nicheDepth,
        );
        r.setUUID(rad.uuid);
        return r;
      })
    : Array.isArray(cloned.radiators)
      ? cloned.radiators.map((rad: any) => {
          const r = new RadiatorModel(
            new Vector3(rad.position.x, rad.position.y, rad.position.z ?? 0),
            rad.length,
            rad.depth,
            rad.angle,
            createAttributes(rad.attributes),
            undefined,
            rad.nicheDepth,
          );
          r.setUUID(rad.uuid);
          return r;
        })
      : [];

  const radB: RadiatorModel[] = Array.isArray(cloned.sides?.B?.radiators)
    ? cloned.sides.B.radiators.map((rad: any) => {
        const r = new RadiatorModel(
          new Vector3(rad.position.x, rad.position.y, rad.position.z ?? 0),
          rad.length,
          rad.depth,
          rad.angle,
          createAttributes(rad.attributes),
          undefined,
          rad.nicheDepth,
        );
        r.setUUID(rad.uuid);
        return r;
      })
    : [];

  wall.sides.A.radiators = radA;
  wall.sides.B.radiators = radB;

  return wall;
}

function createPartitionWall(raw: any): PartitionWallModel {
  const startPoint = new Vector3(raw.startPoint.x, raw.startPoint.y, raw.startPoint.z ?? 0);
  const endPoint = new Vector3(raw.endPoint.x, raw.endPoint.y, raw.endPoint.z ?? 0);

  const doors = (raw.doors ?? []).map((door: any) => {
    const d = new DoorModel(
      new Vector3(door.position.x, door.position.y, door.position.z ?? 0),
      door.length,
      door.depth,
      door.angle,
      door.openingDirection,
      door.orientation,
      createAttributes(door.attributes),
    );
    d.setUUID(door.uuid);
    return d;
  });

  const windows = (raw.windows ?? []).map((win: any) => {
    const w = new WindowModel(
      new Vector3(win.position.x, win.position.y, win.position.z ?? 0),
      win.length,
      win.depth,
      win.angle,
      createAttributes(win.attributes),
    );
    w.setUUID(win.uuid);
    return w;
  });

  const passages = (raw.passages ?? []).map((passage: any) => {
    const p = new PassageModel(
      new Vector3(passage.position.x, passage.position.y, passage.position.z ?? 0),
      passage.length,
      passage.depth,
      passage.angle,
      createAttributes(passage.attributes),
    );
    p.setUUID(passage.uuid);
    return p;
  });

  const wall = new PartitionWallModel(
    startPoint,
    endPoint,
    raw.depth,
    windows,
    doors,
    [], 
    createAttributes(raw.attributes),
    raw.uuid,
    passages,
  );

  const radA: RadiatorModel[] = Array.isArray(raw.sides?.A?.radiators)
    ? raw.sides.A.radiators.map((rad: any) => {
        const r = new RadiatorModel(
          new Vector3(rad.position.x, rad.position.y, rad.position.z ?? 0),
          rad.length,
          rad.depth,
          rad.angle,
          createAttributes(rad.attributes),
          undefined,
          rad.nicheDepth,
        );
        r.setUUID(rad.uuid);
        return r;
      })
    : [];

  const radB: RadiatorModel[] = Array.isArray(raw.sides?.B?.radiators)
    ? raw.sides.B.radiators.map((rad: any) => {
        const r = new RadiatorModel(
          new Vector3(rad.position.x, rad.position.y, rad.position.z ?? 0),
          rad.length,
          rad.depth,
          rad.angle,
          createAttributes(rad.attributes),
          undefined,
          rad.nicheDepth,
        );
        r.setUUID(rad.uuid);
        return r;
      })
    : [];

  wall.sides.A.radiators = radA;
  wall.sides.B.radiators = radB;

  return wall;
}

function makeBoundaryBucketKey(roomUuid: string, spaceUuid: string, boundaryUuid: string) {
  return `${roomUuid}::${spaceUuid}::${boundaryUuid}`;
}

function buildRawBoundaryEntry(edgeData: any, rawWallMap: Map<string, any>, rawGeomMap: Map<string, RawWallGeom>): RawBoundaryEntry | null {
  const wallUuid = edgeData?.wall?.uuid ?? edgeData?.wall;
  if (!wallUuid) return null;

  const rawWall = rawWallMap.get(wallUuid);
  const geom = rawGeomMap.get(wallUuid);
  if (!rawWall || !geom) return null;
  if (isPartitionRaw(rawWall)) return null;

  const side: Side = edgeData.side === 'B' ? 'B' : 'A';
  const reversed = !!edgeData.reversed;
  const t0 = typeof edgeData.t0 === 'number' ? clamp01(edgeData.t0) : 0;
  const t1 = typeof edgeData.t1 === 'number' ? clamp01(edgeData.t1) : 1;

  const faceBaseStart = side === 'A' ? geom.innerStart : geom.outerStart;
  const faceBaseEnd = side === 'A' ? geom.innerEnd : geom.outerEnd;

  const rawP0 = pointOnSegment(faceBaseStart, faceBaseEnd, t0);
  const rawP1 = pointOnSegment(faceBaseStart, faceBaseEnd, t1);

  const faceTraversalStart = reversed ? rawP1.clone() : rawP0.clone();
  const faceTraversalEnd = reversed ? rawP0.clone() : rawP1.clone();

  const centerShift = geom.outward.clone().multiplyScalar(side === 'A' ? geom.depth / 2 : -geom.depth / 2);
  const centerTraversalStart = faceTraversalStart.clone().add(centerShift);
  const centerTraversalEnd = faceTraversalEnd.clone().add(centerShift);

  return {
    edgeUuid: edgeData.uuid,
    wallUuid,
    side,
    reversed,
    // Explicit points are NOT used as the primary geometry.
    // They are only a fallback for degenerate/insane intersections.
    explicitStart: edgeData.startPoint ? vec(edgeData.startPoint) : null,
    explicitEnd: edgeData.endPoint ? vec(edgeData.endPoint) : null,
    faceTraversalStart,
    faceTraversalEnd,
    centerTraversalStart,
    centerTraversalEnd,
  };
}

function orderRawBoundaryEntries(entries: RawBoundaryEntry[]): RawBoundaryEntry[] {
  if (entries.length < 3) return entries;

  const remaining = entries.slice(1);
  const loop = [entries[0]];
  let cursor = entries[0].faceTraversalEnd.clone();

  while (remaining.length > 0) {
    let found = -1;
    let flip = false;

    for (let i = 0; i < remaining.length; i++) {
      if (samePt(cursor, remaining[i].faceTraversalStart)) {
        found = i;
        break;
      }

      if (samePt(cursor, remaining[i].faceTraversalEnd)) {
        found = i;
        flip = true;
        break;
      }
    }

    if (found === -1) {
      // Keep all entries. Do not invent a fallback loop from space.walls.
      loop.push(...remaining);
      break;
    }

    const picked = remaining.splice(found, 1)[0];

    if (!flip) {
      loop.push(picked);
      cursor = picked.faceTraversalEnd.clone();
    } else {
      loop.push({
        ...picked,
        reversed: !picked.reversed,
        explicitStart: picked.explicitEnd ? picked.explicitEnd.clone() : null,
        explicitEnd: picked.explicitStart ? picked.explicitStart.clone() : null,
        faceTraversalStart: picked.faceTraversalEnd.clone(),
        faceTraversalEnd: picked.faceTraversalStart.clone(),
        centerTraversalStart: picked.centerTraversalEnd.clone(),
        centerTraversalEnd: picked.centerTraversalStart.clone(),
      });
      cursor = picked.faceTraversalStart.clone();
    }
  }

  return loop;
}

function buildBoundaryUsageBuckets(
  buildingData: IMultiRoomBuilding,
  rawWallMap: Map<string, any>,
  rawGeomMap: Map<string, RawWallGeom>,
): BoundaryUsageBucket[] {
  const buckets: BoundaryUsageBucket[] = [];

  buildingData.levels.forEach((level) => {
    level.rooms.forEach((room) => {
      room.spaces.forEach((space) => {
        (space.boundaries ?? []).forEach((boundary: any) => {
          if (!isPhysicalBoundaryType(boundary.type)) return;
          if (!Array.isArray(boundary.edges) || boundary.edges.length < 3) return;

          const rawEntries = boundary.edges
            .map((edgeData: any) => buildRawBoundaryEntry(edgeData, rawWallMap, rawGeomMap))
            .filter(Boolean) as RawBoundaryEntry[];

          if (rawEntries.length < 3) return;

          const loop = orderRawBoundaryEntries(rawEntries);
          if (loop.length < 3) return;

          const faceCorners: Vector3[] = [];
          const centerCorners: Vector3[] = [];

          for (let i = 0; i < loop.length; i++) {
            const prev = loop[(i - 1 + loop.length) % loop.length];
            const curr = loop[i];

            const explicitFaceFallback =
              prev.explicitEnd && curr.explicitStart && !hasInsanePoint(prev.explicitEnd) && !hasInsanePoint(curr.explicitStart)
                ? prev.explicitEnd.clone().add(curr.explicitStart).multiplyScalar(0.5)
                : prev.faceTraversalEnd.clone().add(curr.faceTraversalStart).multiplyScalar(0.5);

            const faceCorner = safeLineIntersection2D(
              prev.faceTraversalStart,
              prev.faceTraversalEnd,
              curr.faceTraversalStart,
              curr.faceTraversalEnd,
              explicitFaceFallback,
            );

            const centerFallback = prev.centerTraversalEnd.clone().add(curr.centerTraversalStart).multiplyScalar(0.5);

            const centerCorner = safeLineIntersection2D(
              prev.centerTraversalStart,
              prev.centerTraversalEnd,
              curr.centerTraversalStart,
              curr.centerTraversalEnd,
              centerFallback,
            );

            faceCorners.push(faceCorner);
            centerCorners.push(centerCorner);
          }

          let records: WallUsageRecord[] = loop.map((entry, index) => ({
            boundaryUuid: boundary.uuid,
            edgeUuid: entry.edgeUuid,
            wallUuid: entry.wallUuid,
            side: entry.side,
            reversed: entry.reversed,
            faceCornerStart: faceCorners[index].clone(),
            faceCornerEnd: faceCorners[(index + 1) % faceCorners.length].clone(),
            centerCornerStart: centerCorners[index].clone(),
            centerCornerEnd: centerCorners[(index + 1) % centerCorners.length].clone(),
          }));

          records = ensureClockwiseUsageRecords(records);

          buckets.push({
            key: makeBoundaryBucketKey(room.uuid, space.uuid, boundary.uuid),
            roomUuid: room.uuid,
            spaceUuid: space.uuid,
            boundaryUuid: boundary.uuid,
            rawEdgeCount: boundary.edges.length,
            records,
          });
        });
      });
    });
  });

  return buckets;
}

function resolveFinalCenterlineEndpoints(
  rawGeomMap: Map<string, RawWallGeom>,
  buckets: BoundaryUsageBucket[],
): Map<string, { start: Vector3; end: Vector3 }> {
  const byWall = new Map<string, Array<{ point: Vector3; t: number }>>();

  buckets.forEach((bucket) => {
    bucket.records.forEach((rec) => {
      const resolvedWallUuid = rec.resolvedWallUuid ?? rec.wallUuid;

      // Split usages are created explicitly later from their own record.
      // The canonical legacy wall should only be resolved from usages that still
      // really share the original uuid.
      if (resolvedWallUuid !== rec.wallUuid) return;

      const geom = rawGeomMap.get(rec.wallUuid);
      if (!geom) return;

      const tA = projectParamOnLine(geom.centerStartBase, geom.centerEndBase, rec.centerCornerStart);
      const tB = projectParamOnLine(geom.centerStartBase, geom.centerEndBase, rec.centerCornerEnd);

      if (!byWall.has(rec.wallUuid)) byWall.set(rec.wallUuid, []);
      byWall.get(rec.wallUuid)!.push({ point: rec.centerCornerStart.clone(), t: tA });
      byWall.get(rec.wallUuid)!.push({ point: rec.centerCornerEnd.clone(), t: tB });
    });
  });

  const out = new Map<string, { start: Vector3; end: Vector3 }>();

  rawGeomMap.forEach((geom, wallUuid) => {
    const pts = byWall.get(wallUuid) ?? [];

    if (pts.length === 0) {
      out.set(wallUuid, {
        start: geom.centerStartBase.clone(),
        end: geom.centerEndBase.clone(),
      });
      return;
    }

    pts.sort((a, b) => a.t - b.t);

    out.set(wallUuid, {
      start: pts[0].point.clone(),
      end: pts[pts.length - 1].point.clone(),
    });
  });

  return out;
}


function pointLineDistance2D(p: Vector3, a: Vector3, b: Vector3): number {
  const ab = b.clone().sub(a);
  const len2 = ab.x * ab.x + ab.y * ab.y;
  if (len2 < EPS) return Math.sqrt((p.x - a.x) ** 2 + (p.y - a.y) ** 2);

  const ap = p.clone().sub(a);
  const t = Math.max(0, Math.min(1, (ap.x * ab.x + ap.y * ab.y) / len2));
  const proj = new Vector3(a.x + ab.x * t, a.y + ab.y * t, 0);
  return Math.sqrt((p.x - proj.x) ** 2 + (p.y - proj.y) ** 2);
}

function centerSegmentsCompatible(a: WallUsageRecord, b: WallUsageRecord): boolean {
  const a0 = a.centerCornerStart;
  const a1 = a.centerCornerEnd;
  const b0 = b.centerCornerStart;
  const b1 = b.centerCornerEnd;

  const lenA = a0.distanceTo(a1);
  const lenB = b0.distanceTo(b1);
  if (lenA < EPS || lenB < EPS) return false;

  const dirA = a1.clone().sub(a0).normalize();
  const dirB = b1.clone().sub(b0).normalize();

  // Same line, either same direction or opposite direction.
  if (Math.abs(dirA.dot(dirB)) < 0.999) return false;

  const collinearTolerance = 0.03;
  if (pointLineDistance2D(b0, a0, a1) > collinearTolerance) return false;
  if (pointLineDistance2D(b1, a0, a1) > collinearTolerance) return false;

  const endpointTolerance = 0.05;
  const direct =
    a0.distanceTo(b0) <= endpointTolerance &&
    a1.distanceTo(b1) <= endpointTolerance;

  const flipped =
    a0.distanceTo(b1) <= endpointTolerance &&
    a1.distanceTo(b0) <= endpointTolerance;

  return direct || flipped;
}

function hash32(input: string, seed: number): string {
  let h = (0x811c9dc5 ^ seed) >>> 0;

  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }

  return h.toString(16).padStart(8, '0');
}

function deterministicUuid(input: string): string {
  const hex = `${hash32(input, 0)}${hash32(input, 1)}${hash32(input, 2)}${hash32(input, 3)}`.split('');

  // UUID v4 shape, deterministic payload.
  hex[12] = '4';
  const variant = parseInt(hex[16], 16);
  hex[16] = ((variant & 0x3) | 0x8).toString(16);

  const s = hex.join('');
  return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20, 32)}`;
}

function makeResolvedUsageWallUuid(rec: WallUsageRecord, bucket: BoundaryUsageBucket, index: number) {
  return deterministicUuid(`v2-wall-split:${rec.wallUuid}:${bucket.roomUuid}:${bucket.spaceUuid}:${bucket.boundaryUuid}:${rec.edgeUuid ?? index}`);
}

/**
 * Decides whether a legacy V2 wall can stay shared in the new centerline system.
 *
 * Critical rule:
 * If one legacy wall is used by multiple spaces but their migrated centerline
 * segments do not describe the same segment, we must NOT force them onto the
 * same StandardWallModel. Otherwise one room keeps correct dimensions while
 * another room gets open corners / broken floor topology.
 *
 * In that case we create a separate wall per boundary-edge usage.
 */
function assignResolvedWallUuidsForV2Buckets(buckets: BoundaryUsageBucket[]) {
  const recordsBySourceWall = new Map<string, Array<{ bucket: BoundaryUsageBucket; record: WallUsageRecord; index: number }>>();

  buckets.forEach((bucket) => {
    bucket.records.forEach((record, index) => {
      if (!recordsBySourceWall.has(record.wallUuid)) recordsBySourceWall.set(record.wallUuid, []);
      recordsBySourceWall.get(record.wallUuid)!.push({ bucket, record, index });
    });
  });

  recordsBySourceWall.forEach((items, wallUuid) => {
    if (items.length <= 1) {
      items.forEach(({ record }) => {
        record.resolvedWallUuid = wallUuid;
      });
      return;
    }

    let allCompatible = true;

    for (let i = 0; i < items.length && allCompatible; i++) {
      for (let j = i + 1; j < items.length; j++) {
        if (!centerSegmentsCompatible(items[i].record, items[j].record)) {
          allCompatible = false;
          break;
        }
      }
    }

    if (allCompatible) {
      items.forEach(({ record }) => {
        record.resolvedWallUuid = wallUuid;
      });
      return;
    }

    items.forEach(({ bucket, record, index }) => {
      record.resolvedWallUuid = makeResolvedUsageWallUuid(record, bucket, index);
    });
  });
}

function buildFallbackPhysicalBoundaryFromStandardWalls(spaceWalls: Wall[]): PhysicalBoundaryModel[] {
  const boundaryWalls = spaceWalls.filter(isStandardWallInstance);
  if (boundaryWalls.length < 3) return [];

  const remaining = [...boundaryWalls];
  const ordered: Array<{ wall: StandardWallModel; reversed: boolean }> = [];

  const first = remaining.shift()!;
  ordered.push({ wall: first, reversed: false });

  let cursor = first.endPoint.clone();

  while (remaining.length > 0) {
    let found = -1;
    let reversed = false;

    for (let i = 0; i < remaining.length; i++) {
      if (samePt(cursor, remaining[i].startPoint)) {
        found = i;
        break;
      }

      if (samePt(cursor, remaining[i].endPoint)) {
        found = i;
        reversed = true;
        break;
      }
    }

    if (found === -1) break;

    const next = remaining.splice(found, 1)[0];
    ordered.push({ wall: next, reversed });
    cursor = reversed ? next.startPoint.clone() : next.endPoint.clone();
  }

  if (ordered.length < 3) return [];

  const pb = new PhysicalBoundaryModel();

  const edges = ordered.map(({ wall, reversed }) => {
    const e = new BoundaryEdge(wall, 0, 1, 'A', reversed);
    e.setPoints(
      reversed ? wall.endPoint.clone() : wall.startPoint.clone(),
      reversed ? wall.startPoint.clone() : wall.endPoint.clone(),
    );
    return e;
  });

  pb.setEdges(edges);
  pb.setWalls(Array.from(new Map(edges.map((e) => [e.wall.uuid, e.wall])).values()));
  return [pb];
}

function setExactFaceSegmentForIfc(wall: StandardWallModel, geom: RawWallGeom, side: Side, faceStart: Vector3, faceEnd: Vector3) {
  const innerStart = side === 'A'
    ? faceStart.clone()
    : faceStart.clone().add(geom.outward.clone().multiplyScalar(-geom.depth));

  const innerEnd = side === 'A'
    ? faceEnd.clone()
    : faceEnd.clone().add(geom.outward.clone().multiplyScalar(-geom.depth));

  const outerStart = side === 'B'
    ? faceStart.clone()
    : faceStart.clone().add(geom.outward.clone().multiplyScalar(geom.depth));

  const outerEnd = side === 'B'
    ? faceEnd.clone()
    : faceEnd.clone().add(geom.outward.clone().multiplyScalar(geom.depth));

  if ((wall as any).setOuterStartPoint) (wall as any).setOuterStartPoint(outerStart);
  else (wall as any).outerStartPoint = outerStart;

  if ((wall as any).setOuterEndPoint) (wall as any).setOuterEndPoint(outerEnd);
  else (wall as any).outerEndPoint = outerEnd;

  if ((wall as any).setOuterStartPoint2) (wall as any).setOuterStartPoint2(innerStart);
  else (wall as any).outerStartPoint2 = innerStart;

  if ((wall as any).setOuterEndPoint2) (wall as any).setOuterEndPoint2(innerEnd);
  else (wall as any).outerEndPoint2 = innerEnd;
}

function createBoundaryEdgeFromRecord(
  rec: WallUsageRecord,
  wall: StandardWallModel,
  geom?: RawWallGeom,
  exactFaceForIfc = false,
): BoundaryEdge | null {
  if (
    hasInsanePoint(rec.faceCornerStart) ||
    hasInsanePoint(rec.faceCornerEnd) ||
    hasInsanePoint(rec.centerCornerStart) ||
    hasInsanePoint(rec.centerCornerEnd)
  ) {
    return null;
  }

  if (exactFaceForIfc && geom) {
    setExactFaceSegmentForIfc(wall, geom, rec.side, rec.faceCornerStart, rec.faceCornerEnd);

    const edge = new BoundaryEdge(wall, 0, 1, rec.side, false);
    if (rec.edgeUuid) edge.setUUID(rec.edgeUuid);
    edge.setPoints(rec.faceCornerStart.clone(), rec.faceCornerEnd.clone());
    return edge;
  }

  const tA = projectParamOnLine(wall.startPoint, wall.endPoint, rec.centerCornerStart);
  const tB = projectParamOnLine(wall.startPoint, wall.endPoint, rec.centerCornerEnd);

  const t0 = Math.min(tA, tB);
  const t1 = Math.max(tA, tB);
  const reversed = tA > tB;

  const edge = new BoundaryEdge(wall, t0, t1, rec.side, reversed);
  if (rec.edgeUuid) edge.setUUID(rec.edgeUuid);
  edge.setPoints(rec.faceCornerStart.clone(), rec.faceCornerEnd.clone());

  return edge;
}

function collectAssetsToFetch(buildingData: IMultiRoomBuilding, buildingAny: any) {
  const models2DToFetch: Model2DModel[] = [];
  const productsToFetch: ProductModel[] = [];
  const elementsToFetch: ElementModel[] = [];
  const floorsToFetch: FloorModel[] = [];

  const model2DUuids = new Set<string>();
  const productUuids = new Set<string>();
  const elementUuids = new Set<string>();

  const collectModel2D = (m: any) => {
    if (!m || (m.uuid && model2DUuids.has(m.uuid))) return;

    const model = new Model2DModel(
      m.id,
      m.name,
      m.filePath,
      m.fileFormat,
      m.fileSize,
      1,
      m.dimensions.depth,
      createObject(m.object),
      new Vector3(m.position.x, m.position.y, m.position.z),
      m.rotation,
      m.texture,
      createAttributes(m.attributes),
    );
    model.setUUID(m.uuid);
    models2DToFetch.push(model);
    if (m.uuid) model2DUuids.add(m.uuid);
  };

  const collectProduct = (p: any) => {
    if (!p || (p.uuid && productUuids.has(p.uuid))) return;

    const product = new ProductModel(
      String(p.id),
      p.name,
      p.svgPath,
      1,
      p.dimensions.height ?? 0,
      p.dimensions.depth,
      new Vector3(p.position.x, p.position.y, p.position.z),
      p.rotation,
      p.fillColor,
      p.strokeColor,
      p.strokeWidth,
      p.texture,
      createAttributes(p.attributes),
      createObjects(p.objects),
      p.description,
      p.manufacturer,
    );
    product.setUUID(p.uuid);
    productsToFetch.push(product);
    if (p.uuid) productUuids.add(p.uuid);
  };

  const collectElement = (e: any) => {
    if (!e || (e.uuid && elementUuids.has(e.uuid))) return;

    const element = new ElementModel(
      e.type,
      e.length,
      e.width,
      new Vector3(e.position.x, e.position.y, e.position.z),
      e.rotation,
      e.texture,
      e.description,
      e.useAsTextField,
      e.fontSize,
      e.object ? createObject(e.object) : undefined,
      createAttributes(e.attributes),
      e.sideC,
      e.triangleAngle,
    );
    element.setUUID(e.uuid);
    elementsToFetch.push(element);
    if (e.uuid) elementUuids.add(e.uuid);
  };

  const collectFloor = (f: any) => {
    if (!f) return;

    const tiles = f.tiles
      ? new TilesModel(
          f.tiles.length,
          f.tiles.width,
          f.tiles.rotation,
          f.tiles.jointWidth,
          f.tiles.jointColor,
          createAttributes(f.tiles.attributes),
        )
      : undefined;

    const floor = new FloorModel(tiles, f.texture, createAttributes(f.attributes));
    floor.setUUID(f.uuid);
    floorsToFetch.push(floor);
  };

  buildingData.levels.forEach((level) => {
    level.rooms.forEach((room) => {
      room.spaces.forEach((space) => {
        (space.models2D ?? []).forEach(collectModel2D);
        (space.products ?? []).forEach(collectProduct);
        (space.elements ?? []).forEach(collectElement);
        collectFloor(space.floor);
      });
    });

    const lvlAny = level as any;
    (lvlAny.models2D ?? []).forEach(collectModel2D);
    (lvlAny.products ?? []).forEach(collectProduct);
    (lvlAny.elements ?? []).forEach(collectElement);
  });

  (buildingAny.models2D ?? []).forEach(collectModel2D);
  (buildingAny.products ?? []).forEach(collectProduct);
  (buildingAny.elements ?? []).forEach(collectElement);

  return {
    models2DToFetch,
    productsToFetch,
    elementsToFetch,
    floorsToFetch,
  };
}

export const importPlanJsonV2 = async (
  jsonString: string,
): Promise<{
  plan: PlanModel;
  building: IBuildingProviderContext;
  measurements: SimpleMeasurementModel[];
  cameraPositionSprites: CameraPositionSpriteModel[];
  hotspots: HotspotModel[];
  nonFetchableEntities: Array<NonFetchableEntity>;
  relations: Array<IRelation>;
}> => {
  const data = JSON.parse(jsonString);
  const planData = data.plan;
  const buildingData = planData.building as IMultiRoomBuilding;
  const buildingAny = buildingData as any;
  const compassRotation = planData.compassRotation ?? 0;
  const relations = planData.relations ?? [];
  const auth = true;
  const nonFetchableEntities: NonFetchableEntity[] = [];

  const rawWallMap = collectAllRawWalls(buildingData, buildingAny);

  const rawGeomMap = new Map<string, RawWallGeom>();
  rawWallMap.forEach((raw, uuid) => {
    if (isPartitionRaw(raw)) return;
    rawGeomMap.set(uuid, computeRawWallGeom(raw));
  });

  const boundaryUsageBuckets = buildBoundaryUsageBuckets(buildingData, rawWallMap, rawGeomMap);
  assignResolvedWallUuidsForV2Buckets(boundaryUsageBuckets);

  const splitSourceWallUuids = new Set<string>();
  const resolvedPhysicalWallUuids = new Set<string>();
  const usageCountByResolvedWallUuid = new Map<string, number>();

  boundaryUsageBuckets.forEach((bucket) => {
    bucket.records.forEach((rec) => {
      const resolvedWallUuid = rec.resolvedWallUuid ?? rec.wallUuid;
      resolvedPhysicalWallUuids.add(resolvedWallUuid);
      usageCountByResolvedWallUuid.set(resolvedWallUuid, (usageCountByResolvedWallUuid.get(resolvedWallUuid) ?? 0) + 1);
      if (resolvedWallUuid !== rec.wallUuid) {
        splitSourceWallUuids.add(rec.wallUuid);
      }
    });
  });

  const isPartitionWallModel = (wall: Wall | undefined | null): wall is PartitionWallModel =>
    !!wall && (wall instanceof PartitionWallModel || wall.type === EWall.PARTITION || (wall as any).type === 'partition');

  const shouldKeepWallInGlobalCollections = (wallUuid: string, wall: Wall | undefined | null): boolean => {
    if (!wall?.uuid) return false;
    if (isPartitionWallModel(wall)) return true;
    if (resolvedPhysicalWallUuids.has(wallUuid)) return true;

    // Original source StandardWalls that were split must not survive as extra
    // global/level/space walls, otherwise their doors/windows appear a second time.
    return !splitSourceWallUuids.has(wallUuid);
  };

  const finalCenterlineByWall = resolveFinalCenterlineEndpoints(rawGeomMap, boundaryUsageBuckets);

  const wallMap = new Map<string, Wall>();
  const standardWallMap = new Map<string, StandardWallModel>();

  rawWallMap.forEach((raw, uuid) => {
    if (isPartitionRaw(raw)) {
      const pw = createPartitionWall(raw);
      wallMap.set(uuid, pw);
      return;
    }

    const geom = rawGeomMap.get(uuid);
    if (!geom) return;

    const finalLine = finalCenterlineByWall.get(uuid) ?? {
      start: geom.centerStartBase.clone(),
      end: geom.centerEndBase.clone(),
    };

    const sw = createStandardWallWithResolvedCenterline(raw, geom, finalLine.start, finalLine.end);
    wallMap.set(uuid, sw);
    standardWallMap.set(uuid, sw);
  });

  // Create per-usage cloned walls for incompatible legacy shared walls.
  // They are intentionally separate wall instances so each space can keep its
  // own exact inner boundary dimensions.
  //
  // Important:
  // Every incompatible usage gets its own generated wall UUID. The original
  // source wall is removed from global/level/space collections later.
  // Only one generated clone per legacy source wall keeps doors/windows/radiators.
  const assetOwnerBySourceWallUuid = new Set<string>();

  boundaryUsageBuckets.forEach((bucket) => {
    bucket.records.forEach((rec) => {
      const resolvedWallUuid = rec.resolvedWallUuid ?? rec.wallUuid;
      if (resolvedWallUuid === rec.wallUuid) return;
      if (standardWallMap.has(resolvedWallUuid)) return;

      const raw = rawWallMap.get(rec.wallUuid);
      const geom = rawGeomMap.get(rec.wallUuid);
      if (!raw || !geom) return;

      const clonedRaw = JSON.parse(JSON.stringify(raw));
      clonedRaw.uuid = resolvedWallUuid;

      const isAssetOwner = !assetOwnerBySourceWallUuid.has(rec.wallUuid);
      assetOwnerBySourceWallUuid.add(rec.wallUuid);

      if (!isAssetOwner) {
        clonedRaw.doors = [];
        clonedRaw.windows = [];
        clonedRaw.passages = [];
        clonedRaw.radiators = [];

        if (clonedRaw.sides?.A) clonedRaw.sides.A.radiators = [];
        if (clonedRaw.sides?.B) clonedRaw.sides.B.radiators = [];
      }

      const sw = createStandardWallWithResolvedCenterline(
        clonedRaw,
        geom,
        rec.centerCornerStart.clone(),
        rec.centerCornerEnd.clone(),
      );

      wallMap.set(resolvedWallUuid, sw);
      standardWallMap.set(resolvedWallUuid, sw);
    });
  });

  const { models2DToFetch, productsToFetch, elementsToFetch, floorsToFetch } = collectAssetsToFetch(buildingData, buildingAny);

  const fetchedModelsRaw = await Promise.all(
    models2DToFetch.map(async (m) =>
      auth
        ? instantiateModel2DAsync(m.id, m.position, m.rotation, m.texture, m)
        : {
            model2D: null,
            textureFetchingSuccesful: false,
            textureId: '-1',
            modelId: String(m.id),
            modelData: m,
          },
    ),
  );
  const models2D: Model2DModel[] = fetchedModelsRaw.map((res) => res.model2D).filter(Boolean);

  const fetchedProductsRaw = await Promise.all(
    productsToFetch.map(async (p) => (auth ? fetchProductById(p.id, p.position, p.rotation, p, p.texture) : null)),
  );
  const products: ProductModel[] = fetchedProductsRaw.map((res) => (res as any)?.product || res).filter(Boolean);

  const elements: ElementModel[] = (
    await Promise.all(elementsToFetch.map(async (e) => instantiateElementAsync(e)))
  ).map((res) => res.element);

  const floors: FloorModel[] = floorsToFetch.map((f) => {
    const floorModel = new FloorModel(f.tiles, f.texture, createAttributes(f.attributes));
    floorModel.setUUID(f.uuid);
    return floorModel;
  });

  const bucketByFullKey = new Map<string, BoundaryUsageBucket>();
  boundaryUsageBuckets.forEach((bucket) => {
    bucketByFullKey.set(bucket.key, bucket);
  });

  const levels: BuildingLevelModel[] = buildingData.levels.map((level) => {
    const levelRooms = level.rooms.map((room) => {
      const roomSpaces = room.spaces.map((space) => {
        const spaceFloor = floors.find((ff) => ff.uuid === space.floor?.uuid) ?? null;

        const spaceProducts = (space.products ?? [])
          .map((p: any) => products.find((pp) => pp.uuid === p.uuid))
          .filter(Boolean) as ProductModel[];

        const spaceElements = (space.elements ?? [])
          .map((e: any) => elements.find((ee) => ee.uuid === e.uuid))
          .filter(Boolean) as ElementModel[];

        const spaceModels2D = (space.models2D ?? [])
          .map((m: any) => models2D.find((mm) => mm.uuid === m.uuid))
          .filter(Boolean) as Model2DModel[];

        const directSpaceWalls = (space.walls ?? [])
          .map((w: any) => wallMap.get(w.uuid))
          .filter(Boolean) as Wall[];

        // For V2 physical rooms, standard walls should come from rebuilt boundary edges.
        // Keep direct partition walls because they are not part of physical boundaries.
        const directPartitionWalls = directSpaceWalls.filter((w) => w instanceof PartitionWallModel || w.type === EWall.PARTITION || (w as any).type === 'partition');

        const rebuiltBoundaries = (space.boundaries ?? [])
          .map((b: any) => {
            if (!isPhysicalBoundaryType(b.type)) {
              const vb = new VirtualBoundaryModel();
              vb.setUUID(b.uuid);
              return vb;
            }

            const bucket = bucketByFullKey.get(makeBoundaryBucketKey(room.uuid, space.uuid, b.uuid));
            if (!bucket || bucket.records.length < 3) return null;

            const pb = new PhysicalBoundaryModel();
            pb.setUUID(b.uuid);

            const edges = bucket.records
              .map((rec) => {
                const resolvedWallUuid = rec.resolvedWallUuid ?? rec.wallUuid;
                const wall = standardWallMap.get(resolvedWallUuid);
                const geom = rawGeomMap.get(rec.wallUuid);
                if (!wall) return null;

                // IFC is stricter than the renderer: if this wall is used by exactly
                // one physical edge, make the serialized wall face exactly equal to
                // the explicit BoundaryEdge polygon segment. This keeps the visual
                // boundary unchanged and avoids moving shared walls.
                const exactFaceForIfc = (usageCountByResolvedWallUuid.get(resolvedWallUuid) ?? 0) === 1;

                return createBoundaryEdgeFromRecord(rec, wall, geom, exactFaceForIfc);
              })
              .filter(Boolean) as BoundaryEdge[];

            if (edges.length !== bucket.records.length || edges.length !== bucket.rawEdgeCount) {
              console.warn('[importPlanJsonV2] Boundary edge count mismatch after rebuild', {
                roomUuid: bucket.roomUuid,
                spaceUuid: bucket.spaceUuid,
                boundaryUuid: bucket.boundaryUuid,
                rawEdgeCount: bucket.rawEdgeCount,
                recordCount: bucket.records.length,
                edgeCount: edges.length,
              });
            }

            if (hasInsaneBoundaryPoint(edges)) {
              console.warn('[importPlanJsonV2] Boundary has insane coordinates after guarded rebuild', {
                roomUuid: bucket.roomUuid,
                spaceUuid: bucket.spaceUuid,
                boundaryUuid: bucket.boundaryUuid,
              });
            }

            if (edges.length < 3) return null;

            pb.setEdges(edges);
            pb.setWalls(Array.from(new Map(edges.map((e) => [e.wall.uuid, e.wall])).values()));
            return pb;
          })
          .filter(Boolean) as Array<PhysicalBoundaryModel | VirtualBoundaryModel>;

        if (rebuiltBoundaries.filter((b) => b instanceof PhysicalBoundaryModel).length === 0) {
          // Keep this only as last-resort fallback. It should not be hit for normal V2 spaces.
          rebuiltBoundaries.push(...buildFallbackPhysicalBoundaryFromStandardWalls(directSpaceWalls));
        }

        const physicalBoundaryWalls = rebuiltBoundaries
          .filter((b): b is PhysicalBoundaryModel => b instanceof PhysicalBoundaryModel)
          .flatMap((b) => b.edges.map((e) => e.wall));

        const syncedSpaceWalls = Array.from(
          new Map([...physicalBoundaryWalls, ...directPartitionWalls].map((w) => [w.uuid, w])).values(),
        );

        const spaceModel = new SpaceModel(
          spaceFloor,
          spaceProducts,
          spaceElements,
          spaceModels2D,
          rebuiltBoundaries,
          syncedSpaceWalls,
        );
        spaceModel.setUUID(space.uuid);
        return spaceModel;
      });

      const roomModel = new RoomModel(room.title, roomSpaces, createAttributes(room.attributes));
      roomModel.setUUID(room.uuid);
      return roomModel;
    });

    const levelWallsSet = new Map<string, Wall>();

    levelRooms.forEach((r) =>
      r.spaces.forEach((s) => {
        s.walls.forEach((w) => {
          if (w?.uuid) levelWallsSet.set(w.uuid, w);
        });

        s.boundaries.forEach((b: any) => {
          if (!(b instanceof PhysicalBoundaryModel)) return;
          b.edges.forEach((e: BoundaryEdge) => {
            if (e?.wall?.uuid) levelWallsSet.set(e.wall.uuid, e.wall);
          });
        });
      }),
    );

    const lvlAny = level as any;

    // Keep level-only walls, including partition walls not used by a space.
    // But do not re-add original source StandardWalls that were split into
    // generated per-space walls.
    (lvlAny.walls ?? []).forEach((w: any) => {
      const mapped = wallMap.get(w.uuid);
      if (mapped?.uuid && shouldKeepWallInGlobalCollections(w.uuid, mapped)) {
        levelWallsSet.set(mapped.uuid, mapped);
      }
    });

    const levelProducts: ProductModel[] =
      (lvlAny.products ?? [])
        .map((p: any) => products.find((pp) => pp.uuid === p.uuid))
        .filter(Boolean) as ProductModel[];

    const levelElements: ElementModel[] =
      (lvlAny.elements ?? [])
        .map((e: any) => elements.find((ee) => ee.uuid === e.uuid))
        .filter(Boolean) as ElementModel[];

    const levelModels2D: Model2DModel[] =
      (lvlAny.models2D ?? [])
        .map((m: any) => models2D.find((mm) => mm.uuid === m.uuid))
        .filter(Boolean) as Model2DModel[];

    const levelModel = new BuildingLevelModel(
      level.level,
      levelRooms,
      new CompassModel(compassRotation),
      Array.from(levelWallsSet.values()),
      levelProducts,
      levelElements,
      levelModels2D,
    );
    levelModel.setUUID(level.uuid);
    return levelModel;
  });

  const importedBuilding = new BuildingModel([]);
  importedBuilding.setLevels(levels);

  const globalWalls = new Map<string, Wall>();

  // Preserve partitions, resolved boundary walls, and true level-only / non-boundary
  // walls. Do not preserve original source StandardWalls that were split.
  wallMap.forEach((w) => {
    if (w?.uuid && shouldKeepWallInGlobalCollections(w.uuid, w)) {
      globalWalls.set(w.uuid, w);
    }
  });

  levels.forEach((lvl) => {
    (lvl.walls ?? []).forEach((w: any) => {
      if (w?.uuid) globalWalls.set(w.uuid, w);
    });

    (lvl.rooms ?? []).forEach((room: any) => {
      (room.spaces ?? []).forEach((space: any) => {
        (space.walls ?? []).forEach((w: any) => {
          if (w?.uuid) globalWalls.set(w.uuid, w);
        });

        (space.boundaries ?? []).forEach((b: any) => {
          if (!(b instanceof PhysicalBoundaryModel)) return;
          (b.edges ?? []).forEach((e: any) => {
            if (e?.wall?.uuid) globalWalls.set(e.wall.uuid, e.wall);
          });
        });
      });
    });
  });

  (importedBuilding as any).walls = Array.from(globalWalls.values());

  const bElements = (buildingAny.elements ?? [])
    .map((e: any) => elements.find((ee) => ee.uuid === e.uuid))
    .filter(Boolean);
  if (bElements.length > 0) (importedBuilding as any).elements = bElements;

  const bProducts = (buildingAny.products ?? [])
    .map((p: any) => products.find((pp) => pp.uuid === p.uuid))
    .filter(Boolean);
  if (bProducts.length > 0) (importedBuilding as any).products = bProducts;

  const bModels2D = (buildingAny.models2D ?? [])
    .map((m: any) => models2D.find((mm) => mm.uuid === m.uuid))
    .filter(Boolean);
  if (bModels2D.length > 0) (importedBuilding as any).models2D = bModels2D;

  const measurements: SimpleMeasurementModel[] = planData.annotations?.[0]?.measurements?.map(deserializeMeasurement) ?? [];
  const cameraPositionSprites: CameraPositionSpriteModel[] =
    planData.annotations?.[0]?.cameraPositionSprites?.map(
      (s: any) => new CameraPositionSpriteModel(s.id, new Vector3(s.position.x, s.position.y, s.position.z), s.spaceUuid ?? null),
    ) ?? [];
  const hotspots: HotspotModel[] = planData.annotations?.[0]?.hotspots
    ? await Promise.all(planData.annotations[0].hotspots.map(deserializeHotspot))
    : [];

  const importedProviderBuilding = transformMultiRoomStructure(
    importedBuilding,
    compassRotation,
    nonFetchableEntities,
    relations,
  );

  const normalized = normalizeBuildingTopology(importedProviderBuilding, {
    debug: false,
    mutate: true,
    preserveImportedBoundaryGeometry: true,
    preserveBoundarySides: true,
    strictFaceValidation: false,
  });

  if (!normalized.isValid) {
    console.warn('Topology issues after importPlanJsonV2:', normalized.issues);
  }

  const plan = new PlanModel(
    planData.name,
    planData.id,
    planData.kojeId,
    planData.kojeLevel,
    planData.eventId,
    planData.dataObjectId,
    planData.showroomId,
    APP_VERSION,
    normalized.building,
    JSON_VERSION,
    planData.region ?? '',
  );

  return {
    plan,
    building: normalized.building,
    measurements,
    cameraPositionSprites,
    hotspots,
    nonFetchableEntities,
    relations,
  };
};
